import { ref, computed } from '@vue/composition-api';
import { getFullName } from '@/util/user';
import { useAuthUser } from '@/composables/use-auth-user';
import { createOrUpdateGoodsOfferDecision, GoodsOfferDecisionStatus, GoodsOfferDecisionType, } from '@/api/v2/requests/goods-offers';
import { SystemRole } from '@/api/requests/users';
const ROLE_ACCEPT_MAP = {
    [GoodsOfferDecisionType.PLACE]: SystemRole.PLACE_MANAGER,
    [GoodsOfferDecisionType.STRUCTURE]: SystemRole.STRUCTURE_MANAGER,
    [GoodsOfferDecisionType.CENTRAL]: SystemRole.BRAND_ADMIN,
};
export function useGoodsOfferAccept(ctx, params) {
    const { authUserId, systemRole, isObjektoAdmin } = useAuthUser();
    const loading = ref(false);
    const showConfirmModal = ref(false);
    const modalContent = ref(null);
    const offerDecision = computed(() => {
        const offer = params.goodsOffer.value;
        if (!offer || !offer.decisions || !Array.isArray(offer.decisions))
            return null;
        return (offer.decisions.find((d) => d.type === params.level.value) || null);
    });
    const isReadOnly = computed(() => params.isReadOnly?.value === true);
    const canUserTakeAction = computed(() => {
        if (isReadOnly.value)
            return false;
        const decision = offerDecision.value;
        const requiredRole = ROLE_ACCEPT_MAP[params.level.value];
        const hasPermission = systemRole.value === requiredRole || isObjektoAdmin.value;
        const canTakeAction = !decision || decision.decision === GoodsOfferDecisionStatus.REVERTED;
        return hasPermission && canTakeAction;
    });
    const canUserRevertAction = computed(() => {
        if (isReadOnly.value)
            return false;
        const decision = offerDecision.value;
        if (!decision || !decision.user)
            return false;
        return decision.user.id === authUserId.value || isObjektoAdmin.value;
    });
    const status = computed(() => offerDecision.value?.decision);
    const formattedDate = computed(() => offerDecision.value?.createdAt
        ? new Date(offerDecision.value.createdAt).toLocaleString('pl-PL')
        : '');
    const user = computed(() => offerDecision.value?.user ? getFullName(offerDecision.value.user) : '');
    async function changeStatus(decision) {
        if (!params.goodsOffer.value?.id)
            return;
        loading.value = true;
        try {
            await createOrUpdateGoodsOfferDecision(params.goodsOffer.value.id, {
                decision,
                type: params.level.value,
            });
            ctx.emit('refresh');
        }
        catch (e) {
            console.error('Error changing status:', e);
            ctx.root.$toast.error(ctx.root.$t('goods-offer.change-decision-error'));
        }
        finally {
            loading.value = false;
        }
    }
    async function accept() {
        await changeStatus(GoodsOfferDecisionStatus.ACCEPTED);
    }
    async function reject() {
        await changeStatus(GoodsOfferDecisionStatus.REJECTED);
    }
    async function revert() {
        await changeStatus(GoodsOfferDecisionStatus.REVERTED);
    }
    function runActionWithModal(title, action) {
        showConfirmModal.value = true;
        modalContent.value = {
            title,
            action: async () => {
                loading.value = true;
                try {
                    showConfirmModal.value = false;
                    await action();
                }
                finally {
                    loading.value = false;
                }
            },
        };
    }
    function onCloseModal() {
        showConfirmModal.value = false;
        modalContent.value = null;
    }
    return {
        loading,
        status,
        user,
        formattedDate,
        offerDecision,
        canUserTakeAction,
        canUserRevertAction,
        showConfirmModal,
        modalContent,
        accept,
        reject,
        revert,
        runActionWithModal,
        onCloseModal,
    };
}
